import type { PokerlyWs } from '@/services/pokerly-ws/index.js';
import { useSessionStateStore, RoundStates } from '@/stores/session-state.ts';
import { useParticipantsStore } from '@/stores/participants.ts';
import { useVotingStore } from '@/stores/voting.ts';
import { useSessionInteractionsStore } from '@/stores/session-interactions.ts';
import { useRoundsStore } from '@/stores/rounds-store.ts';
import { useAuthStore } from '@/stores/auth.ts';
import type {
    RoundClosedCallback,
    RoundFinishedCallback,
    RoundReplayCallback,
    SessionUpdatedCallback,
} from '@/services/pokerly-ws/callbacks.js';
import type { ServerSessionState } from '@pokerly/contracts';

/**
 * Composable that initializes all session-related stores and wires up WebSocket event handlers.
 *
 * This acts as a coordination layer that:
 * 1. Initializes the WebSocket connection in the session state store
 * 2. Registers all event handlers from individual stores
 * 3. Handles cross-store coordination (e.g., round events affecting multiple stores)
 *
 * Usage:
 * ```ts
 * const { initSession, updateFromSnapshot } = useSessionInitializer();
 * initSession(ws);
 * ```
 */
export function useSessionInitializer() {
    const sessionState = useSessionStateStore();
    const participantsStore = useParticipantsStore();
    const votingStore = useVotingStore();
    const interactionsStore = useSessionInteractionsStore();

    /**
     * Initialize all session stores with the WebSocket instance and register event handlers.
     */
    function initSession(ws: PokerlyWs) {
        // Set WS in session state (makes it available to all stores via getWs())
        sessionState.setWs(ws);

        // Register participant event handlers
        ws.onUserJoined(participantsStore.onUserJoined);
        ws.onUserLeft(participantsStore.onUserLeft);
        ws.onUserVoted(participantsStore.onUserVoted);
        ws.onUserRoleUpdated(participantsStore.onUserRoleUpdated);

        // Register round event handlers (these coordinate across stores)
        ws.onRoundClosed(handleRoundClosed);
        ws.onRoundFinished(handleRoundFinished);
        ws.onRoundReplay(handleRoundReplay);

        // Register session event handlers
        ws.onSessionUpdated(handleSessionUpdated);
        ws.onSessionInteraction(interactionsStore.onSessionInteraction);
    }

    // --- Round Event Handlers (cross-store coordination) ---

    const handleRoundClosed: RoundClosedCallback = (payload) => {
        sessionState.setRoundIds(payload.roundId, payload.roundReplayId);
        sessionState.setRoundVotingScale(payload.roundVotingScale);
        setRoundStatus(RoundStates.CLOSED);
    };

    const handleRoundFinished: RoundFinishedCallback = (payload) => {
        sessionState.setRoundIds(payload.roundId, payload.roundReplayId);
        sessionState.setRoundVotingScale(payload.roundVotingScale);
        setRoundStatus(RoundStates.ACTIVE);
        participantsStore.clearVotes();
    };

    const handleRoundReplay: RoundReplayCallback = (payload) => {
        sessionState.setRoundIds(payload.roundId, payload.roundReplayId);
        sessionState.setRoundVotingScale(payload.roundVotingScale);
        setRoundStatus(RoundStates.ACTIVE);
        participantsStore.clearVotes();
    };

    const handleSessionUpdated: SessionUpdatedCallback = (payload) => {
        updateFromSnapshot(payload);
    };

    function setRoundStatus(status: typeof RoundStates.ACTIVE | typeof RoundStates.CLOSED) {
        const roundStore = useRoundsStore();
        roundStore.fetch();
        sessionState.setRoundState(status);
        if (status === RoundStates.ACTIVE) {
            votingStore.clearEstimation();
        }
    }

    // --- Snapshot Update (hydrates all stores from server state) ---

    function updateFromSnapshot(snapshot: ServerSessionState) {
        const authStore = useAuthStore();
        const userId = authStore.user?.id;

        participantsStore.setParticipants(snapshot.users);

        sessionState.setSessionStatus(snapshot.sessionStatus);
        sessionState.setVotingScaleAssignment(snapshot.votingScaleId, snapshot.votingScaleSize);
        sessionState.applyRoundStatus(snapshot.roundStatus);
        sessionState.setRoundIds(snapshot.roundId, snapshot.roundReplayId);
        sessionState.setRoundVotingScale(snapshot.roundVotingScale);
        sessionState.setSessionId(snapshot.sessionId);

        const userVote = snapshot.users.find((p) => p.id === userId)?.vote ?? null;
        if (userVote !== null && snapshot.sessionStatus === 'active') {
            votingStore.hydrateEstimation(userVote);
        } else {
            votingStore.clearEstimation();
        }

        if (snapshot.sessionStatus === 'active') {
            const roundStore = useRoundsStore();
            roundStore.fetch();
        }
    }

    // --- Reset all stores ---

    function resetSession() {
        sessionState.$reset();
        participantsStore.$reset();
        votingStore.$reset();
        interactionsStore.$reset();
    }

    return {
        initSession,
        updateFromSnapshot,
        resetSession,
    };
}
